import React, { useState, useEffect, useContext } from "react"
import { Link, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Row,
  Nav,
  NavItem,
  NavLink,
  Card,
  CardBody,
} from "reactstrap"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { classApi } from "apis/ClassListApi"
import { categoryApi } from "apis/CategoryApi"
import { SessionContext } from "context/sessionContext"

const CategoryMaster = () => {
  document.title = "Category Master"
  const { Session } = useContext(SessionContext)
  let session = Session || sessionStorage.getItem("SessionId")
  const navigate = useNavigate()

  const [gradeLevel, setGradeLevel] = useState("subject")
  const [activeTab, setActiveTab] = useState("subject")

  const [subjects, setSubjects] = useState([])
  const [topics, setTopics] = useState([])

  const [subjectType, setSubjectType] = useState({
    value: "scholastic",
    label: "Scholastic",
  })
  const [coScholasticSubjectName, setCoScholasticSubjectName] = useState("")

  const [selectedSubject, setSelectedSubject] = useState(null)
  const [subjectSequence, setSubjectSequence] = useState("")

  const [topicSelectedSubject, setTopicSelectedSubject] = useState(null)
  const [topicName, setTopicName] = useState("")
  const [topicSequence, setTopicSequence] = useState("")

  const [classOptions, setClassOptions] = useState([])
  const [subjectDropdownOptions, setSubjectDropdownOptions] = useState([])
  const [subjectsLoading, setSubjectsLoading] = useState(false)

  const [subjectError, setSubjectError] = useState("")
  const [topicError, setTopicError] = useState("")

  const subjectTypeOptions = [
    { value: "scholastic", label: "Scholastic" },
    { value: "co-scholastic", label: "Co-Scholastic" },
  ]

  // ── Fetch class list on mount ─────────────────────────────────────────────
  useEffect(() => {
    classApi
      .getAllClass()
      .then(res => {
        const classes = res.data?.class || []
        const classDropdown = classes.map(item => ({
          value: item.st_class,
          label: item.st_class.toUpperCase(),
          uid: item.uid,
        }))
        setClassOptions(classDropdown)
      })
      .catch(err => {
        console.error("Failed to fetch classes:", err)
      })
  }, [])

  const markingOptions = [
    { value: "Marks Based", label: "Marks Based" },
    { value: "Grade Based", label: "Grade Based" },
  ]

  const selectStyles = {
    menu: provided => ({
      ...provided,
      backgroundColor: "#fff",
      zIndex: 9999,
    }),
    menuList: provided => ({
      ...provided,
      backgroundColor: "#fff",
      maxHeight: "200px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
      color: "#000",
      cursor: "pointer",
    }),
  }

  const validation = useFormik({
    initialValues: {
      category_name: "",
      class_name: [],
      marking: null,
    },

    validationSchema: Yup.object({
      category_name: Yup.string().required("Please Enter Category Name"),
      class_name: Yup.array()
        .nullable()
        .min(1, "Please Select at least one Class")
        .required("Please Select Class"),
      marking: Yup.object().nullable().required("Please Select Marking"),
    }),

    onSubmit: values => {
      if (subjects.length === 0) {
        Swal.fire({
          title: "Validation Error",
          text: "Please add at least one subject before saving.",
          icon: "warning",
          confirmButtonColor: "#556ee6",
        })
        setActiveTab("subject")
        return
      }

      // Mixed subject type check — cannot mix scholastic and co-scholastic
      const isCoSch = s =>
        s.subject_code === "" || String(s.subject_code).startsWith("CO")
      const hasScholastic = subjects.some(s => !isCoSch(s))
      const hasCoScholastic = subjects.some(s => isCoSch(s))
      if (hasScholastic && hasCoScholastic) {
        Swal.fire({
          title: "Mixed Subject Types",
          html: `You have added both <strong>Scholastic</strong> and <strong>Co-Scholastic</strong> subjects.<br/><br/>
            Please use only <strong>one subject type</strong> — either all Scholastic or all Co-Scholastic subjects.`,
          icon: "warning",
          confirmButtonColor: "#556ee6",
        })
        setActiveTab("subject")
        return
      }

      if (gradeLevel === "topic") {
        if (topics.length === 0) {
          Swal.fire({
            title: "No Topics Added",
            html: `You have selected <strong>Topic Level Grade</strong> but have not added topics to any subject.<br/><br/>
               Please add topics to all subjects, or switch to <strong>Subject Level Grade</strong>.`,
            icon: "warning",
            confirmButtonColor: "#556ee6",
          })
          setActiveTab("topic")
          return
        }

        const subjectsWithoutTopics = subjects.filter(
          sub => !topics.some(t => t.subject.value === sub.subject.value)
        )

        if (subjectsWithoutTopics.length > 0) {
          const missingList = subjectsWithoutTopics
            .map((s, i) => `${i + 1}. ${s.subject.label}`)
            .join("<br/>")

          Swal.fire({
            title: "Topics Missing for Some Subjects",
            html: `You have not added topics for the following subject(s):<br/><br/>
               <strong>${missingList}</strong><br/><br/>
               Please add at least one topic for each subject.`,
            icon: "warning",
            confirmButtonColor: "#556ee6",
          })
          setActiveTab("topic")
          return
        }
      }

      const commonPayload = {
        category_id: "",
        session_id: session ? parseInt(session, 10) : null,
        category_name: values.category_name,
        class: values.class_name.map(c => ({ uid: c.uid, name: c.value })),
        evaluation_type: values.marking.value,
      }

      if (gradeLevel === "subject") {
        const payload = {
          ...commonPayload,
          marking: "Subject Wise",
          subjects: subjects.map(item => {
            const isCo =
              item.subject_code === "" ||
              String(item.subject_code).startsWith("CO")
            const entry = {
              subject_name: item.subject.label,
              sequence_no: Number(item.sequence_no),
              subject_type: isCo ? "CO-SCHOLISTIC" : "SCHOLISTIC",
            }
            if (
              item.subject_code &&
              !String(item.subject_code).startsWith("CO_SC_")
            ) {
              entry.subject_code = item.subject_code
            }
            return entry
          }),
        }

        categoryApi
          .addSubjectWiseCategory(payload)
          .then(res => {
            Swal.fire({
              title: "Created!",
              text: res?.data?.msg || "Category Created Successfully.",
              icon: "success",
              confirmButtonColor: "#556ee6",
            }).then(() => {
              navigate("/view-categories")
            })
            validation.resetForm()
            resetFormState()
          })
          .catch(err => {
            console.error(err)
            Swal.fire({
              title: "Error",
              text:
                err.response?.data?.msg ||
                "An error occurred while creating the category.",
              icon: "error",
              confirmButtonColor: "#556ee6",
            })
          })
      } else {
        const subjectsWithTopics = subjects.map(sub => {
          const isCo =
            sub.subject_code === "" || String(sub.subject_code).startsWith("CO")
          const entry = {
            subject_name: sub.subject.label,
            subject_type: isCo ? "CO-SCHOLISTIC" : "SCHOLISTIC",
            topics: topics
              .filter(t => t.subject.value === sub.subject.value)
              .map(t => ({
                topic_name: t.topic,
                sequence_no: Number(t.sequence_no),
              })),
          }
          if (
            sub.subject_code &&
            !String(sub.subject_code).startsWith("CO_SC_")
          ) {
            entry.subject_code = sub.subject_code
          }
          return entry
        })

        const payload = {
          ...commonPayload,
          marking: "Subject + Topic Wise",
          subjects: subjectsWithTopics,
        }

        categoryApi
          .addSubjectPlusTopicWiseCategory(payload)
          .then(res => {
            Swal.fire({
              title: "Created!",
              text: res?.data?.msg || "Category Created Successfully.",
              icon: "success",
              confirmButtonColor: "#556ee6",
            }).then(() => {
              navigate("/view-categories")
            })
            validation.resetForm()
            resetFormState()
          })
          .catch(err => {
            console.error(err)
            Swal.fire({
              title: "Error",
              text:
                err.response?.data?.msg ||
                "An error occurred while creating the category.",
              icon: "error",
              confirmButtonColor: "#556ee6",
            })
          })
      }
    },
  })

  useEffect(() => {
    const selectedClasses = validation.values.class_name
    if (!selectedClasses || selectedClasses.length === 0) {
      setSubjectDropdownOptions([])
      setSubjects([])
      setTopics([])
      setSelectedSubject(null)
      setTopicSelectedSubject(null)
      setSubjectError("")
      setTopicError("")
      return
    }

    const classIdentifiers = selectedClasses.map(c => c.value)
    setSubjectsLoading(true)

    classApi
      .getSubjectByClass({ st_class: classIdentifiers })
      .then(res => {
        const fetchedSubjects = res.data?.subjects || []
        const options = fetchedSubjects.map(item => ({
          value: item.subject_code,
          label: item.subject_name,
          subject_code: item.subject_code,
        }))
        setSubjectDropdownOptions(options)

        const availableCodes = new Set(options.map(o => o.value))

        // Keep co-scholastic subjects (code starts with "CO" or empty) and valid scholastic ones
        setSubjects(prev =>
          prev.filter(
            s =>
              s.subject_code === "" ||
              String(s.subject_code).startsWith("CO") ||
              availableCodes.has(s.subject.value)
          )
        )
        setTopics(prev => prev.filter(t => availableCodes.has(t.subject.value)))

        setSelectedSubject(prev =>
          prev && availableCodes.has(prev.value) ? prev : null
        )
        setTopicSelectedSubject(prev =>
          prev && availableCodes.has(prev.value) ? prev : null
        )

        setSubjectError("")
        setTopicError("")
      })
      .catch(err => {
        console.error("Failed to fetch subjects by class:", err)
        setSubjectDropdownOptions([])
      })
      .finally(() => {
        setSubjectsLoading(false)
      })
  }, [validation.values.class_name])

  const resetFormState = () => {
    setSubjects([])
    setTopics([])
    setSelectedSubject(null)
    setSubjectSequence("")
    setTopicSelectedSubject(null)
    setTopicName("")
    setTopicSequence("")
    setGradeLevel("subject")
    setActiveTab("subject")
    setSubjectError("")
    setTopicError("")
    setSubjectDropdownOptions([])
    setSubjectType({ value: "scholastic", label: "Scholastic" })
    setCoScholasticSubjectName("")
  }

  // ─── Add Subject ──────────────────────────────────────────────────────────
  const addSubject = () => {
    setSubjectError("")

    if (subjectType.value === "scholastic") {
      if (!selectedSubject || !subjectSequence.trim()) {
        setSubjectError("Please select a subject and enter a sequence number.")
        return
      }

      const duplicateSubject = subjects.find(
        s => s.subject.value === selectedSubject.value
      )
      if (duplicateSubject) {
        setSubjectError(
          `"${selectedSubject.label}" has already been added. Duplicate subjects are not allowed.`
        )
        return
      }

      const duplicateSeq = subjects.find(
        s => String(s.sequence_no) === String(subjectSequence.trim())
      )
      if (duplicateSeq) {
        setSubjectError(
          `Sequence No. "${subjectSequence.trim()}" is already used by "${
            duplicateSeq.subject.label
          }". Please use a unique sequence number.`
        )
        return
      }

      setSubjects(prev => [
        ...prev,
        {
          id: Date.now(),
          subject: selectedSubject,
          subject_code: selectedSubject.subject_code,
          sequence_no: subjectSequence,
        },
      ])
      setSelectedSubject(null)
      setSubjectSequence("")
    } else {
      // co-scholastic
      const trimmedName = coScholasticSubjectName.trim().toUpperCase()

      if (!trimmedName || !subjectSequence.trim()) {
        setSubjectError("Please enter both subject name and sequence number.")
        return
      }

      const duplicateSubject = subjects.find(
        s => s.subject.label.toUpperCase() === trimmedName
      )
      if (duplicateSubject) {
        setSubjectError(
          `"${trimmedName}" has already been added. Duplicate subjects are not allowed.`
        )
        return
      }

      const duplicateSeq = subjects.find(
        s => String(s.sequence_no) === String(subjectSequence.trim())
      )
      if (duplicateSeq) {
        setSubjectError(
          `Sequence No. "${subjectSequence.trim()}" is already used by "${
            duplicateSeq.subject.label
          }". Please use a unique sequence number.`
        )
        return
      }

      const coCode = `CO_SC_${Date.now()}`
      setSubjects(prev => [
        ...prev,
        {
          id: Date.now(),
          subject: { value: coCode, label: trimmedName },
          subject_code: "", // backend expects empty string for co-scholastic
          sequence_no: subjectSequence,
        },
      ])
      setCoScholasticSubjectName("")
      setSubjectSequence("")
    }
  }

  const removeSubject = id => {
    const removedSubject = subjects.find(s => s.id === id)
    setSubjects(subjects.filter(item => item.id !== id))
    if (removedSubject) {
      setTopics(prev =>
        prev.filter(t => t.subject.value !== removedSubject.subject.value)
      )
    }
  }

  // ─── Add Topic ────────────────────────────────────────────────────────────
  const addTopic = () => {
    setTopicError("")

    if (!topicSelectedSubject) {
      setTopicError("Please select a subject before adding a topic.")
      return
    }

    if (!topicName.trim() || !topicSequence.trim()) {
      setTopicError("Please enter both topic name and sequence number.")
      return
    }

    const duplicateTopic = topics.find(
      t =>
        t.subject.value === topicSelectedSubject.value &&
        t.topic.trim().toLowerCase() === topicName.trim().toLowerCase()
    )
    if (duplicateTopic) {
      setTopicError(
        `Topic "${topicName.trim()}" already exists for "${
          topicSelectedSubject.label
        }". Duplicate topics are not allowed within the same subject.`
      )
      return
    }

    const duplicateTopicSeq = topics.find(
      t =>
        t.subject.value === topicSelectedSubject.value &&
        String(t.sequence_no) === String(topicSequence.trim())
    )
    if (duplicateTopicSeq) {
      setTopicError(
        `Sequence No. "${topicSequence.trim()}" is already used by topic "${
          duplicateTopicSeq.topic
        }" under "${
          topicSelectedSubject.label
        }". Please use a unique sequence number.`
      )
      return
    }

    setTopics(prev => [
      ...prev,
      {
        id: Date.now(),
        subject: topicSelectedSubject,
        topic: topicName,
        sequence_no: topicSequence,
      },
    ])

    setTopicSelectedSubject(null)
    setTopicName("")
    setTopicSequence("")
  }

  const removeTopic = id => {
    setTopics(topics.filter(item => item.id !== id))
  }

  // ─── JSX ──────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid>
            <Breadcrumb title="Examination" breadcrumbItem="Category Master" />

            <Form
              onSubmit={e => {
                e.preventDefault()
                validation.handleSubmit()
              }}
            >
              <Row>
                {/* Category Name */}
                <Col lg={4}>
                  <label>Category Name *</label>
                  <Input
                    name="category_name"
                    placeholder="Enter Category Name"
                    value={validation.values.category_name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.category_name &&
                      !!validation.errors.category_name
                    }
                  />
                  <FormFeedback>{validation.errors.category_name}</FormFeedback>
                </Col>

                {/* Class */}
                <Col lg={4}>
                  <label>Class *</label>
                  <Select
                    isMulti
                    classNamePrefix="select2-selection"
                    options={classOptions}
                    placeholder="Select Class(es)"
                    value={validation.values.class_name}
                    onChange={selected =>
                      validation.setFieldValue("class_name", selected || [])
                    }
                    onBlur={() =>
                      validation.setFieldTouched("class_name", true)
                    }
                    styles={selectStyles}
                  />
                  {validation.touched.class_name &&
                    validation.errors.class_name && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        {validation.errors.class_name}
                      </div>
                    )}
                </Col>

                {/* Marking */}
                <Col lg={4}>
                  <label>Marking *</label>
                  <Select
                    classNamePrefix="select2-selection"
                    options={markingOptions}
                    placeholder="Select Marking"
                    value={validation.values.marking}
                    onChange={selected =>
                      validation.setFieldValue("marking", selected)
                    }
                    onBlur={() => validation.setFieldTouched("marking", true)}
                    styles={selectStyles}
                  />
                  {validation.touched.marking && validation.errors.marking && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {validation.errors.marking}
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="mt-4">
                <Col lg={12} className="d-flex align-items-center">
                  <label className="me-4">
                    <input
                      type="radio"
                      name="gradeLevelRadio"
                      checked={gradeLevel === "subject"}
                      onClick={() => {
                        setGradeLevel("subject")
                        setActiveTab("subject")
                      }}
                      onChange={() => {}}
                    />{" "}
                    Subject Level Grade
                  </label>

                  <label className="me-4">
                    <input
                      type="radio"
                      name="gradeLevelRadio"
                      checked={gradeLevel === "topic"}
                      onClick={() => {
                        setGradeLevel("topic")
                        setActiveTab("topic")
                      }}
                      onChange={() => {}}
                    />{" "}
                    Topic Level Grade
                  </label>

                  <div className="ms-auto d-flex align-items-center">
                    <Link to="/view-categories" className="me-2">
                      <Button color="primary">
                        <i className="mdi mdi-eye-outline me-1" />
                        View Categories
                      </Button>
                    </Link>
                    <Button color="success" type="submit">
                      <i className="bx bx-save me-1" />
                      Save Category
                    </Button>
                  </div>
                </Col>
              </Row>

              <Card className="mt-4">
                <CardBody>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        active={activeTab === "subject"}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setActiveTab("subject")
                          setSubjectError("")
                        }}
                      >
                        Add Subject
                      </NavLink>
                    </NavItem>

                    {gradeLevel === "topic" && (
                      <NavItem>
                        <NavLink
                          active={activeTab === "topic"}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setActiveTab("topic")
                            setTopicError("")
                          }}
                        >
                          Add Topic
                        </NavLink>
                      </NavItem>
                    )}
                  </Nav>

                  {/* ── Add Subject Tab ──────────────────────────────────────── */}
                  {activeTab === "subject" && (
                    <>
                      {subjectType.value === "scholastic" &&
                        validation.values.class_name.length === 0 && (
                          <div
                            className="alert alert-warning mt-3 py-2 px-3"
                            style={{ fontSize: "13px" }}
                          >
                            Please select at least one <strong>Class</strong>{" "}
                            above to load the available subjects.
                          </div>
                        )}

                      <Row className="mt-4">
                        {/* Subject Type */}
                        <Col lg={2}>
                          <label>Subject Type *</label>
                          <Select
                            classNamePrefix="select2-selection"
                            options={subjectTypeOptions}
                            value={subjectType}
                            onChange={val => {
                              setSubjectType(val)
                              setSelectedSubject(null)
                              setCoScholasticSubjectName("")
                              setSubjectSequence("")
                              setSubjectError("")
                            }}
                            styles={selectStyles}
                          />
                        </Col>

                        {/* Subject — dropdown for Scholastic, text input for Co-Scholastic */}
                        <Col
                          lg={
                            subjectType.value === "scholastic"
                              ? gradeLevel === "subject"
                                ? 4
                                : 4
                              : 4
                          }
                        >
                          <label>Subject *</label>
                          {subjectType.value === "scholastic" ? (
                            <Select
                              classNamePrefix="select2-selection"
                              options={subjectDropdownOptions}
                              placeholder={
                                subjectsLoading
                                  ? "Loading subjects..."
                                  : validation.values.class_name.length === 0
                                  ? "Select a class first"
                                  : "Select Subject"
                              }
                              value={selectedSubject}
                              onChange={val => {
                                setSelectedSubject(val)
                                setSubjectError("")
                              }}
                              isDisabled={
                                subjectsLoading ||
                                validation.values.class_name.length === 0
                              }
                              isLoading={subjectsLoading}
                              styles={selectStyles}
                            />
                          ) : (
                            <Input
                              value={coScholasticSubjectName}
                              placeholder="Enter Subject Name"
                              onChange={e => {
                                setCoScholasticSubjectName(
                                  e.target.value.toUpperCase()
                                )
                                setSubjectError("")
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault()
                                  addSubject()
                                }
                              }}
                            />
                          )}
                        </Col>

                        {/* Sequence No */}
                        <Col lg={gradeLevel === "subject" ? 2 : 2}>
                          <label>Sequence No *</label>
                          <Input
                            value={subjectSequence}
                            placeholder="Enter Sequence No"
                            disabled={
                              subjectType.value === "scholastic" &&
                              (subjectsLoading ||
                                validation.values.class_name.length === 0)
                            }
                            onChange={e => {
                              setSubjectSequence(e.target.value)
                              setSubjectError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addSubject()
                              }
                            }}
                          />
                        </Col>

                        <Col lg={2} className="d-flex align-items-end">
                          <Button
                            color="primary"
                            onClick={addSubject}
                            disabled={
                              subjectType.value === "scholastic" &&
                              (subjectsLoading ||
                                validation.values.class_name.length === 0)
                            }
                          >
                            Add
                          </Button>
                        </Col>
                      </Row>

                      {subjectError && (
                        <Row className="mt-2">
                          <Col lg={12}>
                            <div
                              className="alert alert-danger py-2 px-3 mb-0"
                              style={{ fontSize: "13px" }}
                            >
                              {subjectError}
                            </div>
                          </Col>
                        </Row>
                      )}

                      <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th width="80">Sr.No</th>
                              <th>Subject</th>
                              {subjectType.value !== "co-scholastic" && <th>Subject Code</th>}
                              <th>Sequence</th>
                              <th width="80">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjects.length > 0 ? (
                              subjects.map((item, index) => {
                                const isCo =
                                  item.subject_code === "" ||
                                  String(item.subject_code).startsWith("CO")
                                return (
                                  <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>{item.subject.label}</td>
                                     {subjectType.value !== "co-scholastic" && (
                                       <td>
                                         {isCo
                                           ? "-"
                                           : item.subject_code ??
                                             item.subject?.subject_code ??
                                             "-"}
                                       </td>
                                     )}
                                    <td>{item.sequence_no}</td>
                                    <td>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeSubject(item.id)}
                                      >
                                        <i className="mdi mdi-delete-outline" />
                                      </Button>
                                    </td>
                                  </tr>
                                )
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={subjectType.value === "co-scholastic" ? 4 : 5}
                                  className="text-center text-muted"
                                >
                                  No Subject Added
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* ── Add Topic Tab ──────────────────────────────────────── */}
                  {activeTab === "topic" && (
                    <>
                      {subjects.length === 0 && (
                        <div
                          className="alert alert-warning mt-3 py-2 px-3"
                          style={{ fontSize: "13px" }}
                        >
                          Please add at least one subject from the{" "}
                          <strong>Add Subject</strong> tab before configuring
                          topics.
                        </div>
                      )}

                      <Row className="mt-4">
                        <Col lg={3}>
                          <label>Subject *</label>
                          <Select
                            classNamePrefix="select2-selection"
                            options={subjects.map(s => s.subject)}
                            value={topicSelectedSubject}
                            onChange={val => {
                              setTopicSelectedSubject(val)
                              setTopicError("")
                            }}
                            placeholder="Select Subject"
                            styles={selectStyles}
                            isDisabled={subjects.length === 0}
                          />
                        </Col>

                        <Col lg={2}>
                          <label>Subject Topic *</label>
                          <Input
                            value={topicName}
                            placeholder="Enter Topic"
                            onChange={e => {
                              setTopicName(e.target.value)
                              setTopicError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addTopic()
                              }
                            }}
                            disabled={subjects.length === 0}
                          />
                        </Col>

                        <Col lg={2}>
                          <label>Sequence No *</label>
                          <Input
                            value={topicSequence}
                            placeholder="Enter Sequence No"
                            onChange={e => {
                              setTopicSequence(e.target.value)
                              setTopicError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addTopic()
                              }
                            }}
                            disabled={subjects.length === 0}
                          />
                        </Col>

                        <Col lg={1} className="d-flex align-items-end">
                          <Button
                            color="primary"
                            onClick={addTopic}
                            disabled={subjects.length === 0}
                          >
                            Add
                          </Button>
                        </Col>
                      </Row>

                      {topicError && (
                        <Row className="mt-2">
                          <Col lg={12}>
                            <div
                              className="alert alert-danger py-2 px-3 mb-0"
                              style={{ fontSize: "13px" }}
                            >
                              {topicError}
                            </div>
                          </Col>
                        </Row>
                      )}

                      <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th width="80">Sr.No</th>
                              <th>Subject</th>
                              <th>Topic</th>
                              <th>Sequence</th>
                              <th width="80">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topics.length > 0 ? (
                              topics.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>{item.subject.label}</td>
                                  <td>{item.topic}</td>
                                  <td>{item.sequence_no}</td>
                                  <td>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => removeTopic(item.id)}
                                    >
                                      <i className="mdi mdi-delete-outline" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="text-center text-muted"
                                >
                                  No Topic Added
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Form>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CategoryMaster
